// api/users.js - User Management API

import { apiClient } from './client';

class UserAPI {
  constructor(client) {
    this.client = client;
  }

  // ========== BASIC CRUD ==========

  async getUsers() {
    return this.client.request('/api/users');
  }

  async getUser(userId) {
    return this.client.request(`/api/users/${userId}`);
  }

  async createUser(username, email, password) {
    return this.client.request('/api/users', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    });
  }

  async updateUser(userId, updates) {
    return this.client.request(`/api/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteUser(userId) {
    return this.client.request(`/api/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // ========== USER PROFILE ==========

  async updateUsername(userId, username) {
    return this.updateUser(userId, { username });
  }

  async updateEmail(userId, email) {
    return this.updateUser(userId, { email });
  }

  async updatePassword(userId, newPassword) {
    return this.updateUser(userId, { password: newPassword });
  }

  // ========== USER PREFERENCES ==========

  async updatePreferences(userId, preferences) {
    return this.updateUser(userId, { 
      preferences: JSON.stringify(preferences) 
    });
  }

  async getUserPreferences(userId) {
    const user = await this.getUser(userId);
    return user.preferences ? JSON.parse(user.preferences) : {};
  }

  // ========== USER STATS ==========

  async getUserStats(userId) {
    const user = await this.getUser(userId);
    
    return {
      username: user.username,
      email: user.email,
      createdAt: user.created_at,
      lastLogin: user.last_login,
      totalCampaigns: user.total_campaigns || 0,
      totalPlaytime: user.total_playtime || 0,
    };
  }

  // ========== AUTHENTICATION HELPERS ==========

  async getUserByEmail(email) {
    const users = await this.getUsers();
    return users.find(u => u.email === email);
  }

  async getUserByUsername(username) {
    const users = await this.getUsers();
    return users.find(u => u.username === username);
  }

  async checkUsernameAvailable(username) {
    const user = await this.getUserByUsername(username);
    return !user;
  }

  async checkEmailAvailable(email) {
    const user = await this.getUserByEmail(email);
    return !user;
  }
}

// Export singleton instance
export const userAPI = new UserAPI(apiClient);

// Also export class
export default UserAPI;